import { Component, Inject, OnInit, OnDestroy, ElementRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DatasetService } from 'src/app/services/dataset.service';
import { DynamicTableService } from 'src/app/services/dynamic-table.service';
import { CellFilterService } from 'src/app/common/bhub-common-module/services/cell-filter.service';
import { Dataset, DataTableSchema, DataSchemaDataType } from 'src/app/services/models/datasets.models';
import { CellFilter, ViewManager, CreatePrePopulatedField } from 'src/app/board/pages/admin/admin-components/custom-views-config/view-builder/models/view-builder.models';
import { DynamicTableFilters, MatchMode } from 'src/app/services/models/dynamic-table.models';
import { Subject, takeUntil, forkJoin, debounceTime } from 'rxjs';

export interface LookupDialogData {
  datasetId: number;
  displayColumns: string[];
  filterColumns: string[];
  valueField: string;
  displayField: string;
  cellFilters?: CellFilter[];
  viewManager?: ViewManager;
  title?: string;
  allowCreate?: boolean;
  startInCreateMode?: boolean; // If true, opens directly in create mode (skips browse/search)
  distinct?: boolean; // Return only distinct rows from the dataset
  excludedFields?: string[]; // Field names to hide from create form (will send null)
  prePopulatedFields?: CreatePrePopulatedField[]; // Fields to pre-populate from context
  presetFilterFields?: string[]; // Field names that always appear as filter rows when dialog opens
  filterDisplayMode?: 'dialog' | 'inline'; // How user filters are displayed (default: dialog)
  inlineFilterMatchMode?: string; // Default match mode for inline filters
}

export interface LookupDialogResult {
  selectedRow: any;
  displayValue: any;
  storedValue: any;
}

interface TableColumn {
  fieldId: number;
  fieldName: string;
  displayName: string;
  dataType: string;
}

interface FilterRow {
  fieldId: number | null;
  fieldName: string;
  value: string;
  dateValue?: Date | null;
  matchMode: MatchMode;
  dataType: string;
  availableMatchModes: { label: string; value: MatchMode }[];
  isPreset?: boolean; // Preset filters can't be removed and persist after clear
}

interface CreateFormField {
  fieldId: number;
  fieldName: string;
  displayName: string;
  dataType: string;
  isRequired: boolean;
  isPrimaryKey: boolean;
  isAutoGenerated: boolean;
  referenceDataId: number | null;
  value: any;
  dateValue?: Date | null;
  referenceOptions?: { value: any; label: string }[];
  isLoadingOptions?: boolean;
}

@Component({
  selector: 'bhub-lookup-dialog',
  standalone: false,
  templateUrl: './lookup-dialog.component.html',
  styleUrl: './lookup-dialog.component.scss'
})
export class LookupDialogComponent implements OnInit, OnDestroy {
  title: string = 'Select an Item';
  columns: TableColumn[] = [];
  displayColumns: TableColumn[] = [];
  filterColumns: TableColumn[] = [];
  tableData: any[] = [];
  isLoading = true;

  // User filters
  filterRows: FilterRow[] = [];
  hasActiveFilters = false;
  filterDisplayMode: 'dialog' | 'inline' = 'dialog';

  // Selection
  selectedRowIndex: number | null = null;
  selectedRow: any = null;

  // Create Mode
  isCreateMode = false;
  allowCreate = false;
  startInCreateMode = false;
  createFormFields: CreateFormField[] = [];
  isCreating = false;
  createError: string | null = null;

  private datasetSchemas: DataTableSchema[] = [];
  private destroy$ = new Subject<void>();
  private inlineFilterApply$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<LookupDialogComponent, LookupDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: LookupDialogData,
    private datasetService: DatasetService,
    private dynamicTableService: DynamicTableService,
    private cellFilterService: CellFilterService,
    private elementRef: ElementRef
  ) {
    this.title = data.title || 'Select an Item';
    this.allowCreate = data.allowCreate || false;
    this.startInCreateMode = data.startInCreateMode || false;
    this.filterDisplayMode = data.filterDisplayMode || 'dialog';

    // Set up debounced inline filter apply
    this.inlineFilterApply$
      .pipe(
        debounceTime(1000),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.applyInlineFilters();
      });
  }

  ngOnInit(): void {
    this.loadDataset();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadDataset(): void {
    this.isLoading = true;

    this.datasetService.getDatasetReferences()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (datasetsMap: Map<number, Dataset>) => {
          const dataset = datasetsMap.get(this.data.datasetId);
          if (dataset) {
            this.setupColumns(dataset.dataTableSchemas);

            // Initialize preset filter rows after columns are set up
            this.initPresetFilters();
            this.focusFirstPresetFilterInput();

            // If startInCreateMode is true, skip loading data and go directly to create mode
            if (this.startInCreateMode) {
              this.isLoading = false;
              this.enterCreateMode();
            } else {
              this.loadData();
            }
          } else {
            console.error('Dataset not found:', this.data.datasetId);
            this.isLoading = false;
          }
        },
        error: (error) => {
          console.error('Error loading dataset:', error);
          this.isLoading = false;
        }
      });
  }

  private setupColumns(schemas: DataTableSchema[]): void {
    this.datasetSchemas = schemas;

    // All columns from schema
    this.columns = schemas
      .filter(schema => !schema.isHidden)
      .map(schema => ({
        fieldId: schema.fieldId,
        fieldName: schema.fieldName,
        displayName: schema.displayName || schema.fieldName,
        dataType: schema.dataType.toString()
      }));

    // Display columns (only configured ones, or all if none configured)
    if (this.data.displayColumns && this.data.displayColumns.length > 0) {
      this.displayColumns = this.columns.filter(col =>
        this.data.displayColumns.includes(col.fieldName)
      );
    } else {
      this.displayColumns = [...this.columns];
    }

    // Filter columns (only configured ones)
    if (this.data.filterColumns && this.data.filterColumns.length > 0) {
      this.filterColumns = this.columns.filter(col =>
        this.data.filterColumns.includes(col.fieldName)
      );
    } else {
      this.filterColumns = [];
    }
  }

  private initPresetFilters(): void {
    const presetFields = this.data.presetFilterFields;
    if (!presetFields || presetFields.length === 0 || this.filterColumns.length === 0) return;

    presetFields.forEach(fieldName => {
      const column = this.filterColumns.find(c => c.fieldName === fieldName);
      if (column) {
        const configuredMatchMode = this.filterDisplayMode === 'inline'
          ? (this.data.inlineFilterMatchMode || '')
          : '';
        const filterRow: FilterRow = {
          fieldId: column.fieldId,
          fieldName: column.fieldName,
          value: '',
          dateValue: null,
          matchMode: (configuredMatchMode || (this.isNumericOrDateType(column.dataType) ? 'exact' : 'partial')) as MatchMode,
          dataType: column.dataType,
          availableMatchModes: this.getAvailableMatchModes(column.dataType),
          isPreset: true
        };
        this.filterRows.push(filterRow);
      }
    });
  }

  private focusFirstPresetFilterInput(): void {
    if (this.filterRows.length === 0) return;

    const doFocus = () => {
      // Target the value input inside the first filter row (works for both dialog and inline modes)
      const firstInput = this.elementRef.nativeElement.querySelector(
        '.filter-row .filter-value input, .inline-filter-chip .inline-filter-value input'
      ) as HTMLElement;
      if (firstInput) {
        firstInput.focus();
        return true;
      }
      return false;
    };

    // Retry with increasing delays to handle dialog animation and change detection timing
    setTimeout(() => { if (!doFocus()) setTimeout(() => doFocus(), 300); }, 0);
  }

  private loadData(): void {
    this.isLoading = true;

    // Build pre-configured cell filters
    const cellFilters = this.cellFilterService.buildCellFilters(this.data.cellFilters, this.data.viewManager || null);

    // Build user filters
    const userFilters = this.buildUserFilters();

    // Merge filters
    const filters = this.mergeFilters(cellFilters, userFilters);

    this.dynamicTableService.selectEntries(this.data.datasetId, 100, undefined, filters, this.data.distinct)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.tableData = this.stringifyRowData(response.rowData || []);
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading lookup data:', error);
          this.tableData = [];
          this.isLoading = false;
        }
      });
  }

  private stringifyRowData(rowData: any[]): any[] {
    return rowData.map(row => {
      const stringifiedRow: any = {};
      for (const key in row) {
        if (row.hasOwnProperty(key)) {
          stringifiedRow[key] = row[key] === null || row[key] === undefined ? '' : row[key];
        }
      }
      return stringifiedRow;
    });
  }

  private buildUserFilters(): DynamicTableFilters | undefined {
    const filters: DynamicTableFilters = {};

    this.filterRows.forEach(filterRow => {
      if (!filterRow.fieldId) return;

      const hasValue = this.isDateType(filterRow.dataType)
        ? filterRow.dateValue != null
        : filterRow.value?.trim();

      if (!hasValue) return;

      let values: (string | number)[] = [];

      if (this.isNumericType(filterRow.dataType)) {
        values = [parseFloat(filterRow.value)];
      } else if (this.isDateType(filterRow.dataType)) {
        values = [filterRow.value.trim()];
      } else {
        values = [filterRow.value.trim()];
      }

      filters[filterRow.fieldId] = {
        values: values,
        matchMode: filterRow.matchMode
      };
    });

    return Object.keys(filters).length > 0 ? filters : undefined;
  }

  private mergeFilters(cellFilters: DynamicTableFilters | undefined, userFilters: DynamicTableFilters | undefined): DynamicTableFilters | undefined {
    if (!cellFilters && !userFilters) return undefined;
    if (!cellFilters) return userFilters;
    if (!userFilters) return cellFilters;
    return { ...cellFilters, ...userFilters };
  }

  // Filter row management
  addFilterRow(): void {
    if (this.filterColumns.length === 0) return;

    this.filterRows.push({
      fieldId: null,
      fieldName: '',
      value: '',
      dateValue: null,
      matchMode: 'partial',
      dataType: '',
      availableMatchModes: []
    });
  }

  removeFilterRow(index: number): void {
    this.filterRows.splice(index, 1);
  }

  onFilterFieldChange(filterRow: FilterRow): void {
    const column = this.filterColumns.find(c => c.fieldName === filterRow.fieldName);
    if (column) {
      filterRow.fieldId = column.fieldId;
      filterRow.dataType = column.dataType;

      // Cache available match modes for this data type
      filterRow.availableMatchModes = this.getAvailableMatchModes(filterRow.dataType);

      // Set default match mode based on type
      if (this.isNumericOrDateType(filterRow.dataType)) {
        filterRow.matchMode = 'exact';
      } else {
        filterRow.matchMode = 'partial';
      }

      filterRow.value = '';
      filterRow.dateValue = null;
    }
  }

  onFilterDateChange(filterRow: FilterRow): void {
    if (filterRow.dateValue) {
      filterRow.value = this.formatDateForFilter(filterRow.dateValue);
    } else {
      filterRow.value = '';
    }
  }

  formatDateForFilter(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T00:00:00`;
  }

  applyFilters(): void {
    this.hasActiveFilters = this.filterRows.some(row => {
      if (!row.fieldId) return false;
      if (this.isDateType(row.dataType)) {
        return row.dateValue != null;
      }
      return row.value?.trim();
    });

    this.selectedRowIndex = null;
    this.selectedRow = null;
    this.loadData();
  }

  clearFilters(): void {
    this.filterRows = [];
    this.hasActiveFilters = false;
    this.selectedRowIndex = null;
    this.selectedRow = null;
    this.loadData();
  }

  // Inline filter methods
  addInlineFilter(): void {
    if (this.filterColumns.length === 0) return;
    this.filterRows.push({
      fieldId: null,
      fieldName: '',
      value: '',
      dateValue: null,
      matchMode: 'exact',
      dataType: '',
      availableMatchModes: []
    });
  }

  removeInlineFilter(index: number): void {
    this.filterRows.splice(index, 1);
    this.applyInlineFilters();
  }

  onInlineFilterFieldChange(filterRow: FilterRow): void {
    const column = this.filterColumns.find(c => c.fieldName === filterRow.fieldName);
    if (column) {
      filterRow.fieldId = column.fieldId;
      filterRow.dataType = column.dataType;
      filterRow.availableMatchModes = this.getAvailableMatchModes(column.dataType);
      const configuredMatchMode = this.data.inlineFilterMatchMode || '';
      filterRow.matchMode = (configuredMatchMode || (this.isNumericOrDateType(column.dataType) ? 'exact' : 'partial')) as MatchMode;
      filterRow.value = '';
      filterRow.dateValue = null;
    }
  }

  onInlineFilterValueChange(): void {
    this.inlineFilterApply$.next();
  }

  onInlineFilterDateChange(filterRow: FilterRow): void {
    if (filterRow.dateValue) {
      filterRow.value = this.formatDateForFilter(filterRow.dateValue);
    } else {
      filterRow.value = '';
    }
    this.applyInlineFilters();
  }

  applyInlineFilters(): void {
    this.hasActiveFilters = this.filterRows.some(row => {
      if (!row.fieldId) return false;
      if (this.isDateType(row.dataType)) {
        return row.dateValue != null;
      }
      return row.value?.trim();
    });

    this.selectedRowIndex = null;
    this.selectedRow = null;
    this.loadData();
  }

  clearInlineFilters(): void {
    this.filterRows = [];
    this.hasActiveFilters = false;
    this.selectedRowIndex = null;
    this.selectedRow = null;
    this.loadData();
  }

  getAvailableMatchModes(dataType: string): { label: string; value: MatchMode }[] {
    const stringModes = [
      { label: 'Starts with', value: 'starts_with' as MatchMode },
      { label: 'Contains', value: 'partial' as MatchMode },
      { label: 'Equals', value: 'exact' as MatchMode }
    ];

    const numericModes = [
      { label: 'Equals', value: 'exact' as MatchMode },
      { label: 'Greater than', value: 'greater_than' as MatchMode },
      { label: 'Less than', value: 'less_than' as MatchMode }
    ];

    if (this.isNumericOrDateType(dataType)) {
      return numericModes;
    }
    return stringModes;
  }

  isNumericOrDateType(dataType: string): boolean {
    const numericTypes = [DataSchemaDataType.Integer, DataSchemaDataType.Decimal, DataSchemaDataType.DateTime];
    return numericTypes.includes(dataType as DataSchemaDataType);
  }

  isNumericType(dataType: string): boolean {
    const numericTypes = [DataSchemaDataType.Integer, DataSchemaDataType.Decimal];
    return numericTypes.includes(dataType as DataSchemaDataType);
  }

  isDateType(dataType: string): boolean {
    return dataType === DataSchemaDataType.DateTime;
  }

  isDateTimeFilterRow(filterRow: FilterRow): boolean {
    return filterRow.dataType === DataSchemaDataType.DateTime;
  }

  // Row selection
  onRowClick(row: any, index: number): void {
    if (this.selectedRowIndex === index) {
      // Deselect
      this.selectedRowIndex = null;
      this.selectedRow = null;
    } else {
      // Select
      this.selectedRowIndex = index;
      this.selectedRow = row;
    }
  }

  isRowSelected(index: number): boolean {
    return this.selectedRowIndex === index;
  }

  // Dialog actions
  onConfirm(): void {
    if (this.selectedRow) {
      const result: LookupDialogResult = {
        selectedRow: this.selectedRow,
        displayValue: this.selectedRow[this.data.displayField],
        storedValue: this.selectedRow[this.data.valueField]
      };
      this.dialogRef.close(result);
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  // Formatting helpers
  formatCellValue(value: any, dataType: string): string {
    if (value === null || value === undefined || value === '') return '';

    if (this.isDateType(dataType)) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString();
      }
    }

    if (this.isNumericType(dataType)) {
      const num = Number(value);
      if (!isNaN(num)) {
        return num.toLocaleString();
      }
    }

    return String(value);
  }

  trackByColumn(index: number, column: TableColumn): string {
    return column.fieldName;
  }

  // Create Mode Methods
  enterCreateMode(): void {
    this.isCreateMode = true;
    this.createError = null;
    this.setupCreateForm();
  }

  exitCreateMode(): void {
    this.isCreateMode = false;
    this.createError = null;
    this.createFormFields = [];
  }

  private setupCreateForm(): void {
    const excludedFields = this.data.excludedFields || [];
    const prePopulatedFields = this.data.prePopulatedFields || [];
    const prePopFieldNames = prePopulatedFields.map(p => p.fieldName);

    // Build form fields from dataset schema
    this.createFormFields = this.datasetSchemas
      .filter(schema => !schema.isHidden)
      .map(schema => {
        const isAutoGenerated = schema.fieldName === 'Hidden_Internal_PrimaryKey' ||
                                (schema.isPrimaryKey && schema.fieldName.toLowerCase().includes('internal'));

        // Check if this field is excluded or pre-populated
        const isExcluded = excludedFields.includes(schema.fieldName);
        const isPrePopulated = prePopFieldNames.includes(schema.fieldName);

        return {
          fieldId: schema.fieldId,
          fieldName: schema.fieldName,
          displayName: schema.displayName || schema.fieldName,
          dataType: schema.dataType.toString(),
          isRequired: !schema.isNullable && !isAutoGenerated,
          isPrimaryKey: schema.isPrimaryKey,
          isAutoGenerated: isAutoGenerated || isExcluded || isPrePopulated,
          referenceDataId: schema.referenceDataId || null,
          value: isPrePopulated ? this.resolvePrePopulatedValue(schema.fieldName, prePopulatedFields) : '',
          dateValue: null,
          referenceOptions: [],
          isLoadingOptions: false
        };
      })
      .filter(field => !field.isAutoGenerated); // Exclude auto-generated, excluded, and pre-populated fields from form

    // Load reference options for fields with referenceDataId
    this.loadReferenceOptions();
  }

  private resolvePrePopulatedValue(fieldName: string, prePopFields: CreatePrePopulatedField[]): any {
    const config = prePopFields.find(p => p.fieldName === fieldName);
    if (!config || !this.data.viewManager?.contextData) return '';

    const contextObj = this.data.viewManager.contextData[config.contextObject];
    if (!contextObj || typeof contextObj !== 'object') return '';

    const value = Array.isArray(contextObj)
      ? contextObj[0]?.[config.contextField]
      : contextObj[config.contextField];

    return value ?? '';
  }

  private loadReferenceOptions(): void {
    const fieldsWithReferences = this.createFormFields.filter(f => f.referenceDataId !== null);

    if (fieldsWithReferences.length === 0) return;

    // Mark fields as loading
    fieldsWithReferences.forEach(field => {
      field.isLoadingOptions = true;
    });

    // Load dataset references first
    this.datasetService.getDatasetReferences()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (datasetsMap: Map<number, Dataset>) => {
          // For each reference field, load its options
          fieldsWithReferences.forEach(field => {
            if (field.referenceDataId) {
              const refDataset = datasetsMap.get(field.referenceDataId);
              if (refDataset) {
                // Find the primary key and a display field
                const pkField = refDataset.dataTableSchemas.find(s => s.isPrimaryKey);
                const displayField = refDataset.dataTableSchemas.find(s =>
                  !s.isHidden && !s.isPrimaryKey && s.dataType.toString() === DataSchemaDataType.String
                ) || pkField;

                if (pkField && displayField) {
                  this.dynamicTableService.selectEntries(field.referenceDataId, 200)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                      next: (response) => {
                        field.referenceOptions = (response.rowData || []).map(row => ({
                          value: row[pkField.fieldName],
                          label: row[displayField.fieldName] || row[pkField.fieldName]
                        }));
                        field.isLoadingOptions = false;
                      },
                      error: () => {
                        field.referenceOptions = [];
                        field.isLoadingOptions = false;
                      }
                    });
                } else {
                  field.isLoadingOptions = false;
                }
              } else {
                field.isLoadingOptions = false;
              }
            }
          });
        },
        error: () => {
          fieldsWithReferences.forEach(field => {
            field.isLoadingOptions = false;
          });
        }
      });
  }

  onCreateFormDateChange(field: CreateFormField): void {
    if (field.dateValue) {
      const date = field.dateValue;
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      field.value = `${year}-${month}-${day}T00:00:00`;
    } else {
      field.value = '';
    }
  }

  isCreateFormValid(): boolean {
    // Check all required fields have values
    return this.createFormFields
      .filter(f => f.isRequired)
      .every(f => {
        if (this.isDateType(f.dataType)) {
          return f.dateValue != null;
        }
        return f.value !== null && f.value !== undefined && String(f.value).trim() !== '';
      });
  }

  createNewEntry(): void {
    if (!this.isCreateFormValid()) {
      this.createError = 'Please fill in all required fields.';
      return;
    }

    this.isCreating = true;
    this.createError = null;

    // Build the entry data from visible form fields
    const entryData: any = {};

    this.createFormFields.forEach(field => {
      let value = field.value;

      // Convert types as needed
      if (this.isNumericType(field.dataType) && value !== '' && value !== null) {
        value = parseFloat(value);
        if (isNaN(value)) {
          value = null;
        }
      } else if (field.dataType === DataSchemaDataType.Boolean) {
        value = value === true || value === 'true';
      }

      entryData[field.fieldName] = value;
    });

    // Add pre-populated field values (hidden from form but sent to backend)
    const prePopulatedFields = this.data.prePopulatedFields || [];
    prePopulatedFields.forEach(prePopField => {
      const resolvedValue = this.resolvePrePopulatedValue(prePopField.fieldName, prePopulatedFields);
      if (resolvedValue !== '') {
        entryData[prePopField.fieldName] = resolvedValue;
      }
    });

    // Submit to API
    this.dynamicTableService.addEntries(this.data.datasetId, [entryData])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (success) => {
          if (success) {
            // Refresh data and switch back to browse mode
            this.isCreating = false;
            this.exitCreateMode();
            this.loadData();

            // Try to auto-select the new entry after data loads
            // We'll find it by matching the field values
            setTimeout(() => {
              this.autoSelectNewEntry(entryData);
            }, 500);
          } else {
            this.isCreating = false;
            this.createError = 'Failed to create entry. Please try again.';
          }
        },
        error: (error) => {
          this.isCreating = false;
          console.error('Error creating entry:', error);
          this.createError = error?.error?.message || 'Failed to create entry. Please try again.';
        }
      });
  }

  private autoSelectNewEntry(entryData: any): void {
    // Find the new entry in the table data by matching key fields
    // Use the display field as a match criterion
    const displayFieldValue = entryData[this.data.displayField];

    if (displayFieldValue) {
      const matchIndex = this.tableData.findIndex(row =>
        row[this.data.displayField] === displayFieldValue
      );

      if (matchIndex >= 0) {
        this.selectedRowIndex = matchIndex;
        this.selectedRow = this.tableData[matchIndex];
      }
    }
  }

  isCreateFieldDateType(field: CreateFormField): boolean {
    return field.dataType === DataSchemaDataType.DateTime;
  }

  isCreateFieldReferenceType(field: CreateFormField): boolean {
    return field.referenceDataId !== null;
  }

  isCreateFieldBooleanType(field: CreateFormField): boolean {
    return field.dataType === DataSchemaDataType.Boolean;
  }

  trackByField(index: number, field: CreateFormField): string {
    return field.fieldName;
  }
}
